import { FlowValueTypeMap } from "../../../constants/modules/dataType";
import { ModuleIOValueTypeEnum } from "@packages/flow-core/module";
import { FlowNodeInputTypeEnum } from "@packages/flow-core/module/node/constants";
import {
  EditInputFieldMap,
  EditNodeFieldType,
} from "@packages/flow-core/module/node/types";
import { Form, Input, Modal, Select, Switch } from "antd";
import { useEffect, useMemo } from "react";

export type FieldEditModalProps = {
  visible?: boolean;
  onConfirm: (data: EditNodeFieldType) => void;
  onClose: () => void;
  data?: EditNodeFieldType;
  field: EditInputFieldMap;
  externalDataTypeSelectList?: {
    label: string;
    value: ModuleIOValueTypeEnum;
  }[];
};
export default function FieldEditModal({
  visible,
  field,
  onClose,
  onConfirm,
  data,
  externalDataTypeSelectList,
}: FieldEditModalProps) {
  const [form] = Form.useForm();
  const valueType = Form.useWatch("valueType", form);

  useEffect(() => {
    if (!data || Object.keys(data ?? {}).length === 0) {
      form.setFieldsValue({
        inputType: FlowNodeInputTypeEnum.target,
        isToolInput: false,
        required: true,
        defaultValue: "",
      });
    } else {
      form.setFieldsValue(data);
    }
  }, [form, data]);

  const inputTypeList = [
    {
      label: "外部数据",
      value: FlowNodeInputTypeEnum.target,
      valueType: ModuleIOValueTypeEnum.string,
    },
    {
      label: "输入框",
      value: FlowNodeInputTypeEnum.input,
      valueType: ModuleIOValueTypeEnum.string,
    },
    {
      label: "段落输入",
      value: FlowNodeInputTypeEnum.textarea,
      valueType: ModuleIOValueTypeEnum.string,
    },
    {
      label: "开关",
      value: FlowNodeInputTypeEnum.switch,
      valueType: ModuleIOValueTypeEnum.boolean,
    },
    {
      label: "知识库选择",
      value: FlowNodeInputTypeEnum.selectedKnowledgeBase,
      valueType: ModuleIOValueTypeEnum.stringArray,
    },
  ].filter(Boolean) as {
    label: string;
    value: FlowNodeInputTypeEnum;
    valueType: ModuleIOValueTypeEnum;
  }[];

  const dataTypeSelectList = useMemo(
    () =>
      externalDataTypeSelectList ??
      Object.values(FlowValueTypeMap)
        .filter(Boolean)
        .map((item) => ({
          label: item!.label,
          value: item!.value,
        })),
    [externalDataTypeSelectList]
  );

  async function handleConfirm() {
    const values = await form.validateFields()!;
    const inputType = inputTypeList.find(
      (item) => item.value === values.inputType
    );
    onConfirm({
      valueType: values.valueType || inputType?.valueType,
      ...values,
    });
  }

  return (
    <Modal
      open={visible}
      okText="确认"
      cancelText="取消"
      onCancel={onClose}
      onOk={handleConfirm}
      afterClose={() => form.resetFields()}
      title="字段编辑"
      destroyOnClose
      width={520}
    >
      <Form
        form={form}
        labelCol={{ span: 4 }}
        colon={false}
        initialValues={{ required: false }}
      >
        {field.inputType && (
          <Form.Item
            label="输入类型"
            name="inputType"
            rules={[{ required: true }]}
          >
            <Select
              options={inputTypeList}
              onSelect={(v, option) => {
                form.setFieldValue("valueType", option.valueType);
              }}
            />
          </Form.Item>
        )}
        {field.isToolInput && (
          <Form.Item
            label="工具参数"
            valuePropName="checked"
            name="isToolInput"
          >
            <Switch />
          </Form.Item>
        )}
        {field.dataType && (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const inputType = getFieldValue(
                "inputType"
              ) as FlowNodeInputTypeEnum;
              if (
                !field.inputType ||
                (field.inputType && inputType !== FlowNodeInputTypeEnum.target)
              ) {
                return (
                  <Form.Item
                    label="数据类型"
                    name="valueType"
                    rules={[{ required: true }]}
                  >
                    <Select options={dataTypeSelectList} />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
        )}
        {field.required && (
          <Form.Item
            label="是否必填"
            valuePropName="checked"
            name="required"
            rules={[{ required: true }]}
          >
            <Switch />
          </Form.Item>
        )}
        {field.name && (
          <Form.Item label="字段名称" name="label" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        )}
        {field.key && (
          <Form.Item label="字段 Key" name="key" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        )}
        {field.description && (
          <Form.Item label="字段描述" name="description">
            <Input.TextArea />
          </Form.Item>
        )}
        {field.defaultValue && (
          <Form.Item
            label="默认值"
            name="defaultValue"
            rules={[
              {
                validator(_, value) {
                  if (
                    valueType === "bool" &&
                    !!value &&
                    !["true", "false"].includes(value)
                  ) {
                    return Promise.reject(
                      `"布尔" 类型的默认值只能为 true 或 false`
                    );
                  }

                  if (
                    valueType === "number" &&
                    value !== "" &&
                    isNaN(Number(value))
                  ) {
                    return Promise.reject(`"数字" 类型的默认值只能为数字`);
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
